import React, { useCallback, useEffect, useRef, useState } from "react"
import styled from "styled-components"
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material"
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts"
import { getNpsInsights } from "services/stat"
import { verifyResponse } from "utils/httpRequest"

const Wrap = styled.div`
  display: grid;
  gap: 18px;
`

const Metrics = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(145px, 1fr));
  gap: 12px;
`

const Metric = styled.div`
  padding: 18px;
  border: 1px solid var(--cms-border);
  border-radius: 12px;
  background: var(--cms-card-bg);
  .label { color: var(--cms-text-secondary); font-size: 12px; text-transform: uppercase; }
  .value { color: var(--cms-text-primary); font-size: 28px; font-weight: 700; margin-top: 4px; }
`

const Panel = styled.div`
  padding: 18px;
  border: 1px solid var(--cms-border);
  border-radius: 12px;
  background: var(--cms-card-bg);
`

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`

const ChartWrap = styled.div`
  width: 100%;
  height: 260px;
`

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "—")

const TIER_OPTIONS = ["free", "silver", "gold", "platinum", "lifetime"]
const TIER_TILE_OPTIONS = ["free", "silver", "gold", "platinum"]

const NpsInsights = ({ dateRange }) => {
  const [loading, setLoading] = useState(false)
  const [insights, setInsights] = useState(null)
  const [error, setError] = useState("")
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [tier, setTier] = useState("")
  const [questionVersion, setQuestionVersion] = useState("")
  const [sortBy, setSortBy] = useState("createdAt")
  const [sortOrder, setSortOrder] = useState("desc")
  const requestSequence = useRef(0)
  const startAt = dateRange?.[0]?.valueOf()
  const endAt = dateRange?.[1]?.valueOf()

  const load = useCallback(async () => {
    if (!startAt || !endAt) return
    const requestId = ++requestSequence.current
    setLoading(true)
    setError("")
    try {
      const response = await getNpsInsights({
        startAt,
        endAt,
        page,
        pageSize: 25,
        search: search.trim(),
        tier,
        questionVersion,
        sortBy,
        sortOrder: sortOrder.toUpperCase(),
      })
      verifyResponse(response)
      if (requestId === requestSequence.current) {
        setInsights(response.data)
      }
    } catch (requestError) {
      if (requestId === requestSequence.current) {
        setError(
          requestError?.response?.data?.message ||
            "NPS insights could not be loaded. Confirm your role has stat/analytics permission."
        )
      }
    } finally {
      if (requestId === requestSequence.current) {
        setLoading(false)
      }
    }
  }, [startAt, endAt, page, search, tier, questionVersion, sortBy, sortOrder])

  useEffect(() => {
    setPage(1)
  }, [startAt, endAt])

  useEffect(() => {
    const timer = setTimeout(load, search ? 350 : 0)
    return () => clearTimeout(timer)
  }, [load, search])

  const summary = insights?.summary || {}
  const activeRangeLabel =
    startAt && endAt
      ? `${new Date(startAt).toLocaleDateString()} – ${new Date(endAt).toLocaleDateString()}`
      : "Select a date range"
  const pages = Math.max(
    1,
    Math.ceil((insights?.pagination?.total || 0) / (insights?.pagination?.pageSize || 25))
  )
  const handleSort = (column) => {
    setPage(1)
    if (sortBy === column) {
      setSortOrder((current) => (current === "asc" ? "desc" : "asc"))
      return
    }
    setSortBy(column)
    setSortOrder(column === "createdAt" ? "desc" : "asc")
  }

  return (
    <Wrap>
      {error && <Alert severity="error">{error}</Alert>}
      <Metrics>
        {[
          ["Responses", summary.total || 0],
          ["Average score", (summary.averageScore || 0).toFixed(1)],
          ["NPS score", summary.npsScore ?? 0],
          ["Promoters (9–10)", summary.promoters || 0],
          ["Passives (7–8)", summary.passives || 0],
          ["Detractors (0–6)", summary.detractors || 0],
        ].map(([label, value]) => (
          <Metric key={label}>
            <div className="label">{label}</div>
            <div className="value">{value}</div>
          </Metric>
        ))}
      </Metrics>

      <Metrics>
        {TIER_TILE_OPTIONS.map((tierName) => {
          const row = (insights?.byTier || []).find((item) => item.tier === tierName)
          return (
            <Metric key={tierName}>
              <div className="label">{tierName}</div>
              <div className="value">{row?.total || 0}</div>
            </Metric>
          )
        })}
      </Metrics>

      <Panel>
        <Typography variant="subtitle1" fontWeight={700} mb={1}>
          Compare question versions
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          Average score and NPS by question version, for responses in {activeRangeLabel}.
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Question text</TableCell>
                <TableCell>Responses</TableCell>
                <TableCell>Average score</TableCell>
                <TableCell>NPS score</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(insights?.byVersion || []).map((row) => (
                <TableRow key={row.versionLabel} hover>
                  <TableCell>{row.versionLabel}</TableCell>
                  <TableCell>{row.questionText || "—"}</TableCell>
                  <TableCell>{row.total}</TableCell>
                  <TableCell>{(row.averageScore || 0).toFixed(1)}</TableCell>
                  <TableCell>{row.npsScore ?? 0}</TableCell>
                </TableRow>
              ))}
              {!insights?.byVersion?.length && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No versioned NPS responses in this range.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>

      <Panel>
        <Typography variant="subtitle1" fontWeight={700} mb={1}>
          Average score over time
        </Typography>
        <ChartWrap>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={insights?.trend || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 10]} width={32} />
              <RechartsTooltip
                formatter={(value, name) => [
                  name === "averageScore" ? Number(value).toFixed(1) : value,
                  name === "averageScore" ? "Average score" : "Responses",
                ]}
              />
              <Line type="monotone" dataKey="averageScore" name="averageScore" stroke="#7957ff" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartWrap>
      </Panel>

      <Panel>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
          mb={1.5}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Responses
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Showing responses submitted during {activeRangeLabel}
            </Typography>
          </Box>
          {loading && <CircularProgress size={20} />}
        </Stack>
        {loading && insights && <LinearProgress sx={{ mb: 1.5 }} />}
        <Filters>
          <TextField
            size="small"
            label="Search comment or email"
            value={search}
            onChange={(event) => {
              setPage(1)
              setSearch(event.target.value)
            }}
            sx={{ minWidth: 270 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Tier</InputLabel>
            <Select
              label="Tier"
              value={tier}
              onChange={(event) => {
                setPage(1)
                setTier(event.target.value)
              }}
            >
              <MenuItem value="">All tiers</MenuItem>
              {TIER_OPTIONS.map((value) => (
                <MenuItem key={value} value={value}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Version"
            placeholder="e.g. V1"
            value={questionVersion}
            onChange={(event) => {
              setPage(1)
              setQuestionVersion(event.target.value)
            }}
            sx={{ minWidth: 140 }}
          />
          <Button variant="outlined" onClick={load} disabled={loading}>Refresh</Button>
        </Filters>

        {loading && !insights ? (
          <Box display="flex" justifyContent="center" p={5}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ opacity: loading ? 0.55 : 1, transition: "opacity 0.2s" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      ["createdAt", "Submitted"],
                      ["score", "Score"],
                      ["tierSnapshot", "Tier"],
                    ].map(([column, label]) => (
                      <TableCell key={column} sortDirection={sortBy === column ? sortOrder : false}>
                        <TableSortLabel
                          active={sortBy === column}
                          direction={sortBy === column ? sortOrder : "asc"}
                          onClick={() => handleSort(column)}
                        >
                          {label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell>Version</TableCell>
                    <TableCell>Member</TableCell>
                    <TableCell>Comment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(insights?.rows || []).map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{formatDate(row.createdAt)}</TableCell>
                      <TableCell>{row.score}</TableCell>
                      <TableCell>{row.tierSnapshot || "—"}</TableCell>
                      <TableCell>{row.questionVersion || "—"}</TableCell>
                      <TableCell>
                        <div>{row.memberName || "—"}</div>
                        <div style={{ color: "var(--cms-text-secondary)", fontSize: 12 }}>
                          {row.memberEmail}
                        </div>
                      </TableCell>
                      <TableCell>{row.comment || "—"}</TableCell>
                    </TableRow>
                  ))}
                  {!insights?.rows?.length && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No NPS responses in this range.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Pagination count={pages} page={page} onChange={(_, value) => setPage(value)} />
            </Box>
          </>
        )}
      </Panel>
    </Wrap>
  )
}

export default NpsInsights
